import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TASHKENT, loadYandexMaps, type LatLng, type YMap, type YMaps } from '@dorixona/ui'
import { formatPhone, formatTime } from '@dorixona/utils'
import { useMapKeySource } from '../../api/mapsKey'
import { Icon, type IconName } from '../../ui/Icon'
import type { Delivery } from './api'
import { COURIER_STEPS, courierStep, personName } from './model'

type Point = { lat: number; lng: number }

/** Иконка этапа: что происходит — поиск, машина, аптека, заказ в пути, дома. */
const STEP_ICON: Record<(typeof COURIER_STEPS)[number], IconName> = {
  searching: 'i-search',
  courier_assigned: 'i-car',
  at_pickup: 'i-pharmacy',
  picked_up: 'i-box',
  delivered: 'i-home',
}

export interface CourierTrackProps {
  delivery: Delivery
  /** Аптека, из которой везут: туда едет машина до того, как заберёт заказ. */
  pharmacy: Point | null
  /** Куда везут заказ. */
  dest: Point | null
}

/**
 * «Где курьер» в заказе с доставкой (R-44): этапы поездки, машина с номером,
 * водитель со звонком и машина на карте. Статусы и точку присылает служба
 * доставки; заказ перечитывается сам, пока его везут, поэтому карточка
 * обновляется без действий покупателя.
 */
export function CourierTrack({ delivery, pharmacy, dest }: CourierTrackProps) {
  const { t } = useTranslation('account')
  const step = courierStep(delivery.status)
  const courier = delivery.courier
  const done = delivery.status === 'delivered'
  // Пока заказ не забрали, машине важна аптека; потом — адрес покупателя.
  const toPharmacy = step < COURIER_STEPS.indexOf('picked_up')
  const target = toPharmacy ? pharmacy : dest
  const showMap =
    !done && delivery.status !== 'cancelled' && (delivery.car !== null || target !== null)

  return (
    <div className="pp-card od-track">
      <h2>{t('track.title')}</h2>

      {delivery.status === 'cancelled' ? (
        <div className="rx-note" role="status">
          <Icon name="i-info" size={18} />
          {t('track.cancelled')}
        </div>
      ) : (
        // Шкала этапов: кружок с иконкой и подпись под ним, линия между
        // кружками заполняется по мере поездки. Пройденный этап залит,
        // текущий обведён — состояние видно не только по цвету, а диктору его
        // называет скрытая приписка и aria-current.
        <ol className="track-steps" aria-label={t('track.steps')}>
          {COURIER_STEPS.map((name, index) => {
            const passed = index < step || (done && index === step)
            return (
              <li
                key={name}
                className={passed ? 'done' : index === step ? 'now' : undefined}
                aria-current={index === step ? 'step' : undefined}
              >
                <span className="ts-dot" aria-hidden="true">
                  <Icon name={STEP_ICON[name]} size={18} />
                </span>
                <span className="ts-label">{t(`track.step.${name}`)}</span>
                {passed ? <span className="visually-hidden">{t('track.passed')}</span> : null}
              </li>
            )
          })}
        </ol>
      )}

      {/* Машина и водитель: марка во всю ширину, номер — табличкой, как на
          машине, звонок — крупной кнопкой под ними. */}
      {courier && (courier.car || courier.carNumber || courier.name) ? (
        <div className="track-car">
          <span className="track-car-icon" aria-hidden="true">
            <Icon name="i-car" size={22} />
          </span>
          <div className="track-car-text">
            {courier.car ? <b>{courier.car}</b> : null}
            {courier.carNumber ? <span className="od-plate num">{courier.carNumber}</span> : null}
            {courier.name ? (
              <span className="track-driver">
                {t('track.driver', { name: personName(courier.name) })}
              </span>
            ) : null}
          </div>
          {courier.phone ? (
            <a
              className="sc-call track-call num"
              href={`tel:+${courier.phone.replace(/\D/g, '')}`}
              aria-label={t('track.call', { phone: formatPhone(courier.phone) })}
            >
              <Icon name="i-phone" size={15} />
              {formatPhone(courier.phone)}
            </a>
          ) : null}
        </div>
      ) : null}

      {showMap ? (
        <>
          <CourierMap car={delivery.car} target={target} toPharmacy={toPharmacy} />
          <p className="track-note">
            {delivery.car
              ? t('track.updated', { time: formatTime(delivery.car.at) })
              : t('track.noCar')}
          </p>
        </>
      ) : null}
    </div>
  )
}

type MapState = 'loading' | 'ready' | 'failed'

/**
 * Курьер — зелёный кружок с белой легковой машиной, как этап «Едет в аптеку»
 * на шкале. Аптека, куда он едет за заказом, — белый кружок с зелёным
 * аптечным крестом; адрес покупателя — обычная булавка. Метки рисует Яндекс по строке
 * разметки, поэтому значки — те же контуры, что `i-car` и `i-pharmacy` в
 * спрайте, прямо в SVG.
 */
const CAR =
  '<div class="car-at"><div class="car-dot">' +
  '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>' +
  '</svg></div></div>'
const PHARMACY =
  '<div class="car-at"><div class="car-dot store-dot">' +
  '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="M9.8 3h4.4c.66 0 1.2.54 1.2 1.2v4.6H20c.66 0 1.2.54 1.2 1.2v4c0 .66-.54 1.2-1.2 1.2h-4.6v4.6c0 .66-.54 1.2-1.2 1.2H9.8c-.66 0-1.2-.54-1.2-1.2v-4.6H4c-.66 0-1.2-.54-1.2-1.2v-4c0-.66.54-1.2 1.2-1.2h4.6V4.2c0-.66.54-1.2 1.2-1.2z"/>' +
  '</svg></div></div>'
const PIN = '<div class="lpin-at"><div class="lpin"><span></span></div></div>'

/**
 * Карта поездки: машина и куда она едет. Перерисовывается, когда служба
 * присылает новую точку, и держит обе точки в кадре.
 */
interface CourierMapProps {
  car: Delivery['car']
  target: Point | null
  /** Цель — аптека (заказ ещё не забрали), а не адрес покупателя. */
  toPharmacy: boolean
}

function CourierMap({ car, target, toPharmacy }: CourierMapProps) {
  const { t } = useTranslation(['account', 'common'])
  const box = useRef<HTMLDivElement>(null)
  const api = useRef<YMaps | null>(null)
  const map = useRef<YMap | null>(null)
  const [state, setState] = useState<MapState>('loading')
  const keySource = useMapKeySource()

  useEffect(() => {
    let alive = true
    loadYandexMaps(keySource).then(
      (ymaps) => {
        if (!alive) return
        const view = new ymaps.Map(
          box.current as HTMLDivElement,
          { center: TASHKENT, zoom: 12, controls: ['zoomControl'] },
          { suppressMapOpenBlock: true },
        )
        view.behaviors.disable('scrollZoom')
        api.current = ymaps
        map.current = view
        setState('ready')
      },
      () => {
        if (alive) setState('failed')
      },
    )
    return () => {
      alive = false
      map.current?.destroy()
      map.current = null
    }
  }, [keySource])

  const carLat = car?.lat
  const carLng = car?.lng
  const toLat = target?.lat
  const toLng = target?.lng
  useEffect(() => {
    if (state !== 'ready') return
    const ymaps = api.current as YMaps
    const view = map.current as YMap
    view.geoObjects.removeAll()
    const points: LatLng[] = []
    if (toLat !== undefined && toLng !== undefined) {
      points.push([toLat, toLng])
      view.geoObjects.add(
        new ymaps.Placemark(
          [toLat, toLng],
          { hintContent: t('account:track.target') },
          toPharmacy
            ? {
                iconLayout: ymaps.templateLayoutFactory.createClass(PHARMACY),
                iconShape: { type: 'Circle', coordinates: [0, 0], radius: 19 },
              }
            : {
                iconLayout: ymaps.templateLayoutFactory.createClass(PIN),
                iconShape: { type: 'Circle', coordinates: [0, -21], radius: 15 },
              },
        ),
      )
    }
    if (carLat !== undefined && carLng !== undefined) {
      points.push([carLat, carLng])
      view.geoObjects.add(
        new ymaps.Placemark(
          [carLat, carLng],
          { hintContent: t('account:track.car') },
          {
            iconLayout: ymaps.templateLayoutFactory.createClass(CAR),
            iconShape: { type: 'Circle', coordinates: [0, 0], radius: 19 },
            zIndex: 1000,
          },
        ),
      )
    }
    // The map is shown only with at least one point, so there is always a first one.
    const [a, b] = points as [LatLng, LatLng | undefined]
    if (!b) {
      view.setCenter(a, 15)
    } else {
      view.setBounds(
        [
          [Math.min(a[0], b[0]), Math.min(a[1], b[1])],
          [Math.max(a[0], b[0]), Math.max(a[1], b[1])],
        ],
        { checkZoomRange: true, zoomMargin: 40 },
      )
    }
  }, [state, carLat, carLng, toLat, toLng, toPharmacy, t])

  return (
    <div
      ref={box}
      className="track-map"
      role="group"
      aria-label={t('account:track.map')}
      aria-busy={state === 'loading'}
      data-state={state}
    >
      {state === 'failed' ? <span className="map-failed">{t('common:map.failed')}</span> : null}
    </div>
  )
}
